/**
 * Typed directives the backend can emit to interrupt normal request flow.
 *
 * Categories:
 *   Session              — owned by SessionManager
 *   Authorization        — owned by ApiClient (per request)
 *   Gating               — owned by ApiClient (per request)
 *   User-action-required — owned by ApiClient (extensibility surface)
 *
 * See documentation/architecture/session-management-redesign.md for the
 * full taxonomy + handler dispatch rules.
 *
 * The string values are the wire format — the backend emits them verbatim
 * in `detail.directive` and they appear in test fixtures. Renaming a value
 * is a wire-protocol change; renaming only the property name (e.g.
 * `SessionExpired` → `Expired`) is purely a TS rename.
 */
export const DirectiveKind = {
  // Session (SessionManager emits)
  SessionExpired: 'session_expired',
  SessionDead: 'session_dead',
  SessionAnonymous: 'session_anonymous',

  // Authorization (ApiClient emits on 401-post-refresh / 403)
  PermissionDenied: 'permission_denied',
  OrgMismatch: 'org_mismatch',
  PostRefresh401: 'post_refresh_401',

  // Gating (ApiClient emits on 402/403 with structured body)
  OrgDisabled: 'org_disabled',
  TierGated: 'tier_gated',
  SubscriptionExpired: 'subscription_expired',
  QuotaExceeded: 'quota_exceeded',
  // Managed (child) org cut off from access via its manager — manager lapsed
  // or deliberately disabled the link. Mirrors the backend DirectiveKind; a
  // managed org can't self-subscribe, so this is NOT a "go subscribe" prompt.
  ManagedAccessUnavailable: 'managed_access_unavailable',

  // User-action-required
  MfaRequired: 'mfa_required',
  MfaSetupRequired: 'mfa_setup_required',
  EmailVerificationRequired: 'email_verification_required',
  SetupRequired: 'setup_required',
  OrgSelectionRequired: 'org_selection_required',
  TermsAcceptanceRequired: 'terms_acceptance_required',
} as const;

export type DirectiveKind = (typeof DirectiveKind)[keyof typeof DirectiveKind];

export interface ServerDirective {
  kind: DirectiveKind;
  message: string;
  /** Populated by emitter; used by handler for domain-correct redirects. */
  domain?: 'user' | 'admin';

  // Optional UX hints from backend (any/all may be absent)
  redirectTo?: string;
  setupPath?: string;
  ctaText?: string;
  ctaUrl?: string;

  /** Backend extras, typed per kind by downstream consumers. */
  detail?: unknown;
}

/**
 * Shape of a directive emitter's subscribe surface. `SessionManager.onDirective`
 * and (in Phase 2) `ApiClient.onDirective` satisfy this. Lives here so Phase 2
 * merge helpers can type their inputs without importing from a component file.
 */
export type DirectiveSubscribeFn = (
  callback: (directive: ServerDirective) => void,
) => () => void;
